import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  clearToken,
  decodeToken,
  getToken,
  isAuthenticated as hasSession,
  isTokenExpired,
  login as loginRequest,
  logout as logoutRequest,
  redirectToLogin,
  setToken,
} from "./authService";
import { canViewMerchants, isTowpayAdmin } from "./roles";

//  A usable default rather than the throw-outside-provider idiom, because App.test.js
//  renders <App /> bare with no provider and must keep passing.
const AuthContext = createContext({
  isAuthenticated: false,
  email: "",
  roles: [],
  isTowpayAdmin: false,
  canViewMerchants: false,
  login: async () => {},
  logout: async () => {},
});

//  How often to notice that an idle tab's session has lapsed. Every API call also checks,
//  via requireToken; this is only so a tab left open does not look signed in when it is
//  not. platform-services sessions are the shortest user_session_time_out across all the
//  user's tenants, which defaults to 120 minutes, so this is not a rare event.
const EXPIRY_POLL_MS = 60 * 1000;

/**
 * Supplies the signed-in user to the app.
 *
 * @param {object} props
 * @param {string} props.userBaseUrl base URL of the platform-services user service
 * @param {React.ReactNode} props.children
 */
export function AuthProvider({ userBaseUrl, children }) {
  const [token, setTokenState] = useState(() => getToken());

  const signedIn = !!token && !isTokenExpired(token);

  //  Derived from the token held in state rather than re-read from sessionStorage, so a
  //  re-login that swaps one valid token for another actually recomputes.
  const claims = useMemo(() => (signedIn ? decodeToken(token) : null), [signedIn, token]);
  const roles = useMemo(
    () => (claims && Array.isArray(claims.roles) && claims.roles) || [],
    [claims]
  );

  const login = useCallback(
    async (email, password) => {
      const result = await loginRequest(userBaseUrl, email, password);
      setToken(result.token);
      setTokenState(result.token);
      return result;
    },
    [userBaseUrl]
  );

  const logout = useCallback(async () => {
    await logoutRequest(userBaseUrl);
    setTokenState(null);
  }, [userBaseUrl]);

  useEffect(() => {
    if (!signedIn) return undefined;

    const timer = setInterval(() => {
      if (!hasSession()) {
        clearToken();
        setTokenState(null);
        redirectToLogin("expired");
      }
    }, EXPIRY_POLL_MS);

    return () => clearInterval(timer);
  }, [signedIn]);

  const value = useMemo(
    () => ({
      isAuthenticated: signedIn,
      email: (claims && claims.email) || "",
      roles,
      isTowpayAdmin: isTowpayAdmin(roles),
      canViewMerchants: canViewMerchants(roles),
      login,
      logout,
    }),
    [signedIn, claims, roles, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** @returns {object} the signed-in user and the sign in/out actions */
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Renders its children only when signed in. Replaces MSAL's AuthenticatedTemplate.
 *
 * A component rather than a hook so the class components in this app (Merchants,
 * ManageInvites, NavMenu, Layout) can use it without being converted.
 */
export function Authenticated({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : null;
}

/** Renders its children only when signed out. Replaces MSAL's UnauthenticatedTemplate. */
export function Unauthenticated({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? null : <>{children}</>;
}

export default AuthContext;
